import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Chip,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  FormControl,
  InputLabel,
  TableContainer,
} from "@mui/material";
import {
  Search,
  Person,
  AccountBalance,
  Close,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  VerifiedUser,
  Email,
  Phone as PhoneIcon,
  AccountCircle,
  CalendarToday,
  KeyboardArrowDown,
  MoreVert,
} from "@mui/icons-material";
import {
  getAllCustomers23,
  getCustomerBanks,
  addCustomerBanks,
  updateCustomerBanks,
  deleteCustomerBanks,
  verifyBankAccount,
  updateCustomerDetails,
} from "../../../api/customers/customers";
import { banks } from "../../../api/banks";
import { MenuButton } from "../../../components/Menu";

interface Customer {
  user_id: string;
  firstName: string;
  lastName: string;
  phoneNo: string;
  uname: string; 
  altEmail: string; 
  created_at: string;
  last_updated: string;
  middleName?: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface FetchCustomersParams {
  offset: number;
  limit: number;
  search?: string;
}

const CustomerListPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false, loadingMore: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
  const [accountFormData, setAccountFormData] = useState({
    bankName: "",
    accountNumber: "",
    accountName: "",
  });
  const [selectedBankCode, setSelectedBankCode] = useState("");
  const [verifiedAccountName, setVerifiedAccountName] = useState("");
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNo: "",
    uname: "",
    altEmail: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [offset, setOffset] = useState(0);
  const [limit] = useState(400); 
  const [hasMore, setHasMore] = useState(true);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchCustomers = async (params: FetchCustomersParams, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setStatus(prev => ({ ...prev, loadingMore: true }));
    } else {
      setStatus({ loading: true, error: false, loadingMore: false });
    }
    
    try {
      const response = await getAllCustomers23(params.offset, params.limit, params.search);
      
      if (response.data && response.status === true) {
        const fetchedCustomers = response.data || [];
        
        if (isLoadMore) {
          setCustomers(prev => [...prev, ...fetchedCustomers]);
        } else {
          setCustomers(fetchedCustomers);
        }
        
        setTotalCustomers(response?.totalCustomers || fetchedCustomers.length);
        
        if (fetchedCustomers.length < params.limit) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
        
        if (isLoadMore) {
          setOffset(prev => prev + params.limit);
        } else {
          setOffset(params.limit); 
        }
        
        if (!isLoadMore && params.search && fetchedCustomers.length > 0) {
          showSnackbar(`Found ${fetchedCustomers.length} customers matching "${params.search}"`, 'info');
        }
      } else {
        if (!isLoadMore) {
          setStatus({ loading: false, error: true, loadingMore: false });
        } else {
          setStatus(prev => ({ ...prev, loadingMore: false }));
        }
        showSnackbar(response.data?.message || 'Error fetching customers', 'error');
      }
    } catch (error: any) {
      console.error("Error fetching customers:", error);
      if (!isLoadMore) {
        setStatus({ loading: false, error: true, loadingMore: false });
      } else {
        setStatus(prev => ({ ...prev, loadingMore: false }));
      }
      showSnackbar(error?.response?.data?.message || 'Error fetching customers. Please try again.', 'error');
    } finally {
      if (!isLoadMore) {
        setStatus(prev => ({ ...prev, loading: false }));
        setIsInitialLoad(false);
      } else {
        setStatus(prev => ({ ...prev, loadingMore: false }));
      }
    }
  };

  useEffect(() => {
    fetchCustomers({ offset: 0, limit }, false);
  }, []);

  const handleLoadMore = () => {
    if (!status.loadingMore && hasMore) {
      fetchCustomers({ offset, limit, search: searchQuery || undefined }, true);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    
    setOffset(0);
    setHasMore(true);
    
    const timer = setTimeout(() => {
      fetchCustomers({ 
        offset: 0, 
        limit, 
        search: value || undefined 
      }, false);
    }, 500);
    
    return () => clearTimeout(timer);
  };

  const handleOpenAddEditModal = (account: any | null) => {
    setSelectedAccount(account);
    if (account) {
      const bank = banks.find((b) => b.name === account.customer_bank_name);
      setSelectedBankCode(bank ? bank.code : "");
      setAccountFormData({
        bankName: account.customer_bank_name,
        accountNumber: account.customer_account_number,
        accountName: account.customer_account_name,
      });
      setVerifiedAccountName(account.customer_account_name);
    } else {
      setSelectedBankCode("");
      setAccountFormData({
        bankName: "",
        accountNumber: "",
        accountName: "",
      });
      setVerifiedAccountName("");
    }
    setIsAddEditModalOpen(true);
  };

  const handleCloseAddEditModal = () => {
    setSelectedAccount(null);
    setIsAddEditModalOpen(false);
  };

  const handleOpenDeleteConfirm = (account: any) => {
    setSelectedAccount(account);
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setSelectedAccount(null);
    setIsDeleteConfirmOpen(false);
  };

  const handleDeleteBankAccount = async () => {
    if (!selectedCustomer || !selectedAccount) return;

    try {
      await deleteCustomerBanks(selectedCustomer.user_id, selectedAccount.id);
      const response = await getCustomerBanks(selectedCustomer.user_id);
      setBankAccounts(response.data.data || []);
      handleCloseDeleteConfirm();
      showSnackbar('Bank account deleted successfully.', 'success');
    } catch (error) {
      console.error("Error deleting bank account:", error);
      showSnackbar('Error deleting bank account. Please try again.', 'error');
    }
  };

  const handleAddOrUpdateBankAccount = async () => {
    if (!selectedCustomer || !verifiedAccountName) return;

    try {
      if (selectedAccount) {
        await updateCustomerBanks(selectedCustomer.user_id, {
          id: selectedAccount.id,
          bankCode: selectedBankCode,
          accountNumber: accountFormData.accountNumber,
          accountName: verifiedAccountName,
        });
        showSnackbar('Bank account updated successfully.', 'success');
      } else {
        await addCustomerBanks(selectedCustomer.user_id, {
          bankCode: selectedBankCode,
          accountNumber: accountFormData.accountNumber,
          accountName: verifiedAccountName,
        });
        showSnackbar('Bank account added successfully.', 'success');
      }
      const response = await getCustomerBanks(selectedCustomer.user_id);
      setBankAccounts(response.data.data || []);
      handleCloseAddEditModal();
    } catch (error) {
      console.error("Error adding or updating bank account:", error);
      showSnackbar('Error saving bank account. Please try again.', 'error');
    }
  };

  const handleOpenAccountModal = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsAccountModalOpen(true);
    setLoadingBankAccounts(true);
    try {
      const response = await getCustomerBanks(customer.user_id);
      setBankAccounts(response.data.data || []);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      showSnackbar('Error loading bank accounts.', 'error');
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const handleCloseAccountModal = () => {
    setIsAccountModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleOpenEditCustomerModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      firstName: customer.firstName || "",
      middleName: customer.middleName || "",
      lastName: customer.lastName || "",
      phoneNo: customer.phoneNo || "",
      uname: customer.uname || "", 
      altEmail: customer.altEmail || "", 
    });
    setIsEditCustomerModalOpen(true);
  };

  const handleCloseEditCustomerModal = () => {
    setIsEditCustomerModalOpen(false);
    setSelectedCustomer(null);
    setEditFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNo: "",
      uname: "",
      altEmail: "",
    });
  };

  const handleEditCustomer = async () => {
    if (!selectedCustomer) return;
    
    if (!editFormData.firstName.trim() || !editFormData.lastName.trim() || 
        !editFormData.phoneNo.trim() || !editFormData.uname.trim() || !editFormData.altEmail.trim()) {
      showSnackbar('Please fill in all required fields.', 'error');
      return;
    }

    setEditLoading(true);
    try {
      const customerData = {
        firstName: editFormData.firstName.trim(),
        middleName: editFormData.middleName.trim() || undefined,
        lastName: editFormData.lastName.trim(),
        phoneNo: editFormData.phoneNo.trim(),
        uname: editFormData.uname.trim(), 
        altEmail: editFormData.altEmail.trim(), 
        user_id: selectedCustomer.user_id,
      };

      await updateCustomerDetails(customerData);
      
      setCustomers(prevCustomers => 
        prevCustomers.map(customer => 
          customer.user_id === selectedCustomer.user_id 
            ? { 
                ...customer, 
                firstName: customerData.firstName,
                middleName: customerData.middleName || "",
                lastName: customerData.lastName,
                phoneNo: customerData.phoneNo,
                uname: customerData.uname, 
                altEmail: customerData.altEmail, 
                last_updated: new Date().toISOString()
              }
            : customer
        )
      );
      
      showSnackbar(
        `Customer ${customerData.firstName} ${customerData.lastName} updated successfully!`,
        'success'
      );
      
      handleCloseEditCustomerModal();
    } catch (error: any) {
      console.error("Error updating customer:", error);
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || 'Error updating customer. Please try again.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRefresh = () => {
    setOffset(0);
    setHasMore(true);
    fetchCustomers({ offset: 0, limit, search: searchQuery || undefined }, false);
    showSnackbar('Customer list refreshed.', 'info');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return "N/A";
    }
  };

  const getCustomerStatus = (lastUpdated: string) => {
    if (!lastUpdated) return "N/A";
    const lastUpdate = new Date(lastUpdated);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 3600 * 24));
    
    if (daysDiff <= 1) return "Active";
    if (daysDiff <= 7) return "Recent";
    return "Inactive";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active": return "success";
      case "Recent": return "info";
      case "Inactive": return "warning";
      default: return "default";
    }
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <MenuButton />
          <IonTitle>Customer Directory</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonLoading
          isOpen={status.loading}
          message={"Fetching customers..."}
          spinner="circles"
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box sx={{ mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                Customers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Managing {totalCustomers} customers • {customers?.length} loaded
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={status.loading}
                size={isMobile ? "small" : "medium"}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by name, phone, email, username..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
                size="medium"
              />
            </CardContent>
          </Card>
        </Box>

        {/* Responsive Content */}
        <Box>
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>App Details</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers?.map((customer) => (
                    <TableRow key={customer.user_id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }}>
                            {customer.firstName?.charAt(0) || "?"}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {`${customer.firstName} ${customer.middleName || ""} ${customer.lastName}`.trim()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">ID: {customer.user_id?.slice(0, 8)}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.phoneNo}</Typography>
                        <Typography variant="caption" color="text.secondary">{customer.altEmail}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={customer.uname} size="small" variant="outlined" icon={<VerifiedUser sx={{ fontSize: '12px !important' }} />} />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getCustomerStatus(customer.last_updated)}
                          color={getStatusColor(getCustomerStatus(customer.last_updated)) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(customer.last_updated)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Bank Accounts"><IconButton size="small" color="primary" onClick={() => handleOpenAccountModal(customer)}><AccountBalance /></IconButton></Tooltip>
                          <Tooltip title="Edit Customer"><IconButton size="small" color="info" onClick={() => handleOpenEditCustomerModal(customer)}><Edit /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card List View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {customers?.map((customer) => (
              <Card key={customer.user_id} sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', fontWeight: 'bold', width: 48, height: 48 }}>
                      {customer.firstName?.charAt(0) || "?"}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        {`${customer.firstName} ${customer.lastName}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">@{customer.uname}</Typography>
                    </Box>
                    <Chip 
                      label={getCustomerStatus(customer.last_updated)}
                      color={getStatusColor(getCustomerStatus(customer.last_updated)) as any}
                      size="small"
                      sx={{ height: 20, fontSize: '0.625rem' }}
                    />
                  </Stack>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>{customer.phoneNo}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>{customer.altEmail}</Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Updated: {formatDate(customer.last_updated)}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<AccountBalance />} 
                        onClick={() => handleOpenAccountModal(customer)}
                        sx={{ borderRadius: 1.5, textTransform: 'none' }}
                      >
                        Accounts
                      </Button>
                      <Button 
                        size="small" 
                        variant="contained" 
                        startIcon={<Edit />} 
                        onClick={() => handleOpenEditCustomerModal(customer)}
                        sx={{ borderRadius: 1.5, textTransform: 'none' }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Load More Section */}
          {(hasMore || status.loadingMore) && (
            <Box sx={{ mt: 3, textAlign: 'center', pb: 5 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={status.loadingMore}
                sx={{ borderRadius: 2, minWidth: 200, py: 1 }}
                startIcon={status.loadingMore ? <CircularProgress size={20} /> : <KeyboardArrowDown />}
              >
                {status.loadingMore ? "Loading..." : `Load More Customers`}
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                Showing {customers?.length} of {totalCustomers}
              </Typography>
            </Box>
          )}

          {!hasMore && customers?.length > 0 && (
            <Box sx={{ mt: 3, textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">Reached the end of the list</Typography>
            </Box>
          )}

          {customers?.length === 0 && !status.loading && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <AccountCircle sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No customers found</Typography>
            </Box>
          )}
        </Box>

        {/* Bank Accounts Dialog */}
        <Dialog 
          open={isAccountModalOpen} 
          onClose={handleCloseAccountModal} 
          fullWidth 
          maxWidth="md"
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalance />
              <Typography variant="h6">Bank Accounts</Typography>
            </Stack>
            <IconButton onClick={handleCloseAccountModal} sx={{ color: 'white' }}><Close /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1, md: 2 } }}>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {selectedCustomer?.firstName} {selectedCustomer?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{selectedCustomer?.phoneNo}</Typography>
            </Box>

            {loadingBankAccounts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : bankAccounts?.length > 0 ? (
              <Grid container spacing={2}>
                {bankAccounts.map((account) => (
                  <Grid item xs={12} key={account.id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{account.customer_bank_name}</Typography>
                            <Typography variant="h6" sx={{ color: 'primary.main', letterSpacing: 1 }}>{account.customer_account_number}</Typography>
                            <Typography variant="body2" color="text.secondary">{account.customer_account_name}</Typography>
                          </Box>
                          <IconButton color="error" onClick={() => handleOpenDeleteConfirm(account)}><Delete /></IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary">No bank accounts registered</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAccountModal} variant="outlined">Close</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenAddEditModal(null)}>Add New</Button>
          </DialogActions>
        </Dialog>

        {/* Add Bank Account Dialog */}
        <Dialog open={isAddEditModalOpen} onClose={handleCloseAddEditModal} fullWidth maxWidth="xs">
          <DialogTitle>Add Bank Account</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Bank</InputLabel>
                <Select
                  value={selectedBankCode}
                  label="Select Bank"
                  onChange={(e) => setSelectedBankCode(e.target.value)}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.code} value={bank.code}>{bank.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                size="small"
                label="Account Number"
                value={accountFormData.accountNumber}
                onChange={async (e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setAccountFormData({ ...accountFormData, accountNumber: val });
                  if (val.length === 10 && selectedBankCode) {
                    setIsVerifyingAccount(true);
                    try {
                      const name = await verifyBankAccount(val, selectedBankCode);
                      setVerifiedAccountName(name);
                    } catch (err) {
                      setVerifiedAccountName("");
                    } finally {
                      setIsVerifyingAccount(false);
                    }
                  }
                }}
                inputProps={{ maxLength: 10 }}
              />

              <Box sx={{ p: 2, bgcolor: verifiedAccountName ? 'success.50' : 'grey.50', borderRadius: 2, border: '1px solid', borderColor: verifiedAccountName ? 'success.light' : 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block">Account Name</Typography>
                {isVerifyingAccount ? <CircularProgress size={16} /> : <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{verifiedAccountName || "Pending verification..."}</Typography>}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal}>Cancel</Button>
            <Button variant="contained" disabled={!verifiedAccountName} onClick={handleAddOrUpdateBankAccount}>Save Account</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Customer Dialog */}
        <Dialog 
          open={isEditCustomerModalOpen} 
          onClose={handleCloseEditCustomerModal} 
          fullWidth 
          maxWidth="sm"
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>Edit Customer Details</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" value={editFormData.firstName} onChange={(e) => setEditFormData({...editFormData, firstName: e.target.value})} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" value={editFormData.lastName} onChange={(e) => setEditFormData({...editFormData, lastName: e.target.value})} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Middle Name (Optional)" value={editFormData.middleName} onChange={(e) => setEditFormData({...editFormData, middleName: e.target.value})} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number" value={editFormData.phoneNo} onChange={(e) => setEditFormData({...editFormData, phoneNo: e.target.value})} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="App Username" value={editFormData.uname} onChange={(e) => setEditFormData({...editFormData, uname: e.target.value})} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Email Address" type="email" value={editFormData.altEmail} onChange={(e) => setEditFormData({...editFormData, altEmail: e.target.value})} size="small" />
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseEditCustomerModal}>Cancel</Button>
            <Button variant="contained" color="info" disabled={editLoading} onClick={handleEditCustomer}>
              {editLoading ? <CircularProgress size={20} /> : "Update Customer"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={isDeleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
          <DialogTitle>Delete Bank Account?</DialogTitle>
          <DialogContent><Typography>Are you sure you want to remove this account?</Typography></DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeleteBankAccount}>Delete</Button>
          </DialogActions>
        </Dialog>

      </IonContent>
    </IonPage>
  );
};

export default CustomerListPage;
